/**
 * PATH: packages/job-hub/src/jobs/qa/itpCover.ts
 * FILE_ID: JOBHUB-QA-ITP-COVER-001
 * PURPOSE: White ITP cover page. The check table stays in itpDocument.ts.
 * LANDMINE: The cover is emitted BEFORE the .itp-page table, so page-2 padding
 *      does not reach it. The 12mm inset lives on .itp-cover itself.
 */
import { itpBrandLockupHtml } from './itpBrandLockup'

export interface ItpCoverParticipant {
  name: string
  email: string
  role: string
  phone?: string
  userId?: number
}

export interface ItpCoverFields {
  companyName?: string
  address?: string
  phone?: string
  abn?: string
  logoSrc?: string
  logoHeightPx?: number
  clientName?: string
  clientContactName?: string
  clientAddress?: string
  clientPhone?: string
  clientEmail?: string
  siteAddress?: string
  classification?: string
  preparedBy?: string
  generatedAt?: string
  coverImageUrl?: string | null
  coverImageWidthMm?: number | null
  coverImageHeightMm?: number | null
  participants?: ItpCoverParticipant[]
  include?: { product?: boolean; requirement?: boolean; acceptance?: boolean }
}

export function itpHideList(include?: ItpCoverFields['include']): string {
  const flags = include ?? {}
  return (['product', 'requirement', 'acceptance'] as const)
    .filter((key) => !flags[key])
    .join(' ')
}

const ROLE_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  installer: 'Installer',
  pm: 'Project Manager',
  qa_manager: 'QA Manager',
  builder_rep: 'Builder Representative',
  auditor: 'Auditor',
  viewer: 'Viewer',
  owner: 'Owner',
  key_job_person: 'Key job person',
  guest: 'Guest',
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function line(value: string | null | undefined): string {
  const text = (value ?? '').trim()
  return text ? `<div>${escapeHtml(text)}</div>` : ''
}

export function coverRoleLabel(role: string): string {
  const key = role.trim().toLowerCase()
  if (!key) return ''
  if (ROLE_LABELS[key]) return ROLE_LABELS[key]
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function mergeCoverParticipants(
  qa: readonly ItpCoverParticipant[],
  team: readonly ItpCoverParticipant[],
  excludedIds?: ReadonlySet<number>,
): ItpCoverParticipant[] {
  const seen = new Set<string>()
  const rows: ItpCoverParticipant[] = []
  for (const row of [...qa, ...team]) {
    if (row.userId != null && excludedIds?.has(row.userId)) continue
    const email = row.email.trim().toLowerCase()
    if (email) {
      if (seen.has(email)) continue
      seen.add(email)
    }
    rows.push({
      name: row.name.trim(),
      email: row.email.trim(),
      role: coverRoleLabel(row.role),
      phone: (row.phone ?? '').trim(),
      userId: row.userId,
    })
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name))
}

function titleCase(value: string | null | undefined): string {
  const text = (value ?? '').trim()
  if (!text) return ''
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function describeClassification(
  inspection?: { category?: string | null } | null,
  jobContext?: { type?: string | null; detail?: string | null } | null,
): string {
  return [inspection?.category, jobContext?.detail, jobContext?.type]
    .map((value) => titleCase(value))
    .filter(Boolean)
    .join(' · ')
}

function counts(input: ItpCoverDocument) {
  const items = input.checklists.flatMap((list) => list.items)
  const n = (status: string) => items.filter((item) => item.status === status).length
  const notActioned = n('not_started') + n('in_progress')
  return {
    total: items.length,
    complete: items.length - notActioned,
    fa: n('further_action'),
    done: n('done'),
    pass: n('pass'),
    na: n('not_applicable'),
    notActioned,
    fail: n('failed'),
    passAll: n('done') + n('pass'),
  }
}

function formatGenerated(value: string | undefined): string {
  const text = (value ?? '').trim()
  if (!text) return ''
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return text
  return date.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

export interface ItpCoverDocument {
  jobNumber: string | number
  reportTitle?: string
  checklists: Array<{ name?: string; areaName?: string; items: Array<{ status: string }> }>
  cover?: ItpCoverFields
}

function companyBlock(cover: ItpCoverFields): string {
  const parts: string[] = []
  const name = (cover.companyName ?? '').trim()
  if (name) parts.push(`<div class="itp-cover-name">${escapeHtml(name)}</div>`)
  const address = (cover.address ?? '').trim()
  if (address) parts.push(`<div>${escapeHtml(address)}</div>`)
  const abn = (cover.abn ?? '').trim()
  if (abn) parts.push(`<div>ABN/ACN: ${escapeHtml(abn)}</div>`)
  const rawPhone = (cover.phone ?? '').trim()
  const compactPhone = rawPhone.replace(/ /g, '')
  const phone = compactPhone === '+61468918980' || compactPhone === '61468918980'
    ? '03 9872 3333'
    : rawPhone
  if (phone) parts.push(`<div>${escapeHtml(phone)}</div>`)
  return parts.join('')
}

function checklistSummary(checklists: ItpCoverDocument['checklists']): string {
  const groups = new Map<string, string[]>()
  for (const list of checklists) {
    const name = (list.name ?? '').trim()
    if (!name) continue
    const area = (list.areaName ?? '').trim() || 'Checks'
    const rows = groups.get(area) ?? []
    rows.push(name)
    groups.set(area, rows)
  }
  if (groups.size === 0) return ''
  const items = [...groups.entries()].map(([area, names]) => (
    `<li><strong>${escapeHtml(area)}</strong>: ${names.map((name) => escapeHtml(name)).join(', ')}</li>`
  )).join('')
  return `<h2>Checklists</h2><ul class="itp-cover-lists">${items}</ul>`
}

export const ITP_COVER_IMAGE_MIN_MM = 10
export const ITP_COVER_IMAGE_DEFAULT_MM = { width: 186, height: 55 } as const
export const ITP_COVER_IMAGE_MAX_MM = { width: 186, height: 95 } as const

export function itpCoverImageSizeMm(
  width?: number | null,
  height?: number | null,
): { width: number; height: number } {
  const clamp = (value: number | null | undefined, fallback: number, high: number) => {
    if (value == null || !Number.isFinite(value) || value <= 0) return fallback
    return Math.min(high, Math.max(ITP_COVER_IMAGE_MIN_MM, Math.round(value)))
  }
  return {
    width: clamp(width, ITP_COVER_IMAGE_DEFAULT_MM.width, ITP_COVER_IMAGE_MAX_MM.width),
    height: clamp(height, ITP_COVER_IMAGE_DEFAULT_MM.height, ITP_COVER_IMAGE_MAX_MM.height),
  }
}

export function itpCoverImageStyle(width?: number | null, height?: number | null): string {
  const size = itpCoverImageSizeMm(width, height)
  return `width:${size.width}mm;aspect-ratio:${size.width}/${size.height}`
}

export function itpCoverHtml(input: ItpCoverDocument): string {
  const cover = input.cover ?? {}
  const tally = counts(input)
  const title = escapeHtml((input.reportTitle ?? '').trim() || 'Inspection Test Plan')
  const image = (cover.coverImageUrl ?? '').trim()
  const participants = (cover.participants ?? []).filter((row) => row.name || row.email || row.role || row.phone)
  const participantTable = participants.length === 0
    ? '<p class="itp-cover-empty">No participants on this job.</p>'
    : `<table class="itp-cover-people"><thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Phone</th></tr></thead><tbody>${
      participants.map((row) => (
        `<tr><td>${escapeHtml(row.name)}</td><td>${escapeHtml(row.role)}</td><td>${escapeHtml(row.email)}</td><td>${escapeHtml(row.phone ?? '')}</td></tr>`
      )).join('')
    }</tbody></table>`
  const generated = formatGenerated(cover.generatedAt)
  const jobLines = [
    line(String(input.jobNumber ?? '')),
    line(cover.siteAddress),
    line(cover.classification),
    cover.preparedBy?.trim() ? line(`Prepared by ${cover.preparedBy.trim()}`) : '',
    generated ? line(generated) : '',
  ].join('')
  const clientLines = [
    line(cover.clientName),
    line(cover.clientContactName),
    line(cover.clientAddress),
    line(cover.clientPhone),
    line(cover.clientEmail),
  ].join('')
  return `<style>
  .itp-cover { padding: 12mm 12mm 0; page-break-after: always; break-after: page; background: #fff; color: #111; }
  .itp-cover-top { width: 100%; border-collapse: collapse; }
  .itp-cover-top td { vertical-align: top; background: #fff; }
  .itp-cover-company { text-align: right; font-size: 11px; line-height: 1.35; }
  .itp-cover-name { font-weight: 700; font-size: 14px; }
  .itp-brand-left { text-align: left; }
  .itp-brand-left .itp-org-logo { margin: 0; }
  .itp-brand-left .itp-powered-row { justify-content: flex-start; }
  .itp-cover-title { margin-top: 8mm; background: #fff; color: #111; text-align: center; font-size: 18px; font-weight: 700; padding: 8px 12px; }
  .itp-cover-photo { display: block; box-sizing: border-box; max-width: 100%; height: auto; object-fit: cover; margin: 6mm auto 0; border: 1px solid #ddd; }
  .itp-cover-boxes { width: 100%; border-collapse: separate; border-spacing: 4mm 0; margin-top: 4mm; }
  .itp-cover-boxes td { vertical-align: top; border: 1px solid #111; background: #fff; padding: 8px 10px; width: 50%; }
  .itp-cover h2 { font-size: 12px; margin: 0 0 4px; border: 0; background: #fff; }
  .itp-cover-chips { margin-top: 4mm; text-align: center; }
  .itp-chip { display: inline-block; border: 1px solid #111; background: #fff; color: #111; border-radius: 999px; padding: 2px 8px; margin: 0 2px 4px; font-size: 11px; }
  .itp-cover-people { width: 100%; border-collapse: collapse; margin-top: 4px; background: #fff; }
  .itp-cover-people th, .itp-cover-people td { text-align: left; border-bottom: 1px solid #ddd; padding: 4px 8px 4px 0; background: #fff; font-size: 11px; }
  .itp-cover-empty { font-size: 11px; font-style: italic; }
  .itp-cover-lists { margin: 4px 0 0; padding-left: 18px; font-size: 11px; }
  @media screen { .itp-cover { min-height: 297mm; box-sizing: border-box; border-bottom: 1px dashed #cbd5e1; margin-bottom: 6mm; } }
</style>
<section class="itp-cover">
<table class="itp-cover-top"><tbody><tr>
<td>${itpBrandLockupHtml(cover.logoSrc, cover.logoHeightPx, 'left')}</td>
<td class="itp-cover-company">${companyBlock(cover)}</td>
</tr></tbody></table>
<div class="itp-cover-title">${title}</div>
${image ? `<img class="itp-cover-photo" src="${escapeHtml(image)}" alt="" style="${itpCoverImageStyle(cover.coverImageWidthMm, cover.coverImageHeightMm)}" />` : ''}
<table class="itp-cover-boxes"><tbody><tr>
<td><h2>Client</h2>${clientLines}</td>
<td><h2>Job No.</h2>${jobLines}</td>
</tr></tbody></table>
<div class="itp-cover-chips">
<span class="itp-chip">Completion Status - ${tally.complete}/${tally.total}</span>
<span class="itp-chip">Pass - ${tally.passAll}</span>
<span class="itp-chip">Fail - ${tally.fail}</span>
<span class="itp-chip">F A - ${tally.fa}</span>
<span class="itp-chip">N/A - ${tally.na}</span>
</div>
<h2>Participants</h2>
${participantTable}
${checklistSummary(input.checklists)}
</section>`
}
